/**
 * Video Engagement Service
 * Calculates trending status for videos based on today's engagement in the newsfeed
 */

import { 
  collection, 
  query, 
  where, 
  getDocs, 
  Timestamp,
  orderBy
} from 'firebase/firestore';
import { db } from '../firebase';

export interface VideoEngagement {
  videoId: string;
  videoUrl?: string;
  platformId?: string;
  platform?: 'youtube' | 'vimeo' | 'upload';
  engagementScore: number;
  reactions: number;
  comments: number;
  shares: number;
  postsCount: number;
}

/**
 * Extract YouTube video ID from URL
 */
export const extractYouTubeVideoId = (url: string): string | null => {
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
  const match = url.match(regExp);
  return (match && match[2].length === 11) ? match[2] : null;
};

/**
 * Extract Vimeo video ID from URL
 */
export const extractVimeoVideoId = (url: string): string | null => {
  const regExp = /vimeo\.com\/(\d+)/;
  const match = url.match(regExp);
  return match ? match[1] : null;
};

/**
 * Normalize video URL/ID for matching
 */
export const normalizeVideoIdentifier = (video: {
  videoUrl?: string;
  platformId?: string;
  platform?: string;
}): string | null => {
  if (video.platformId && video.platform) {
    return `${video.platform}:${video.platformId}`;
  }
  if (video.videoUrl) {
    const youtubeId = extractYouTubeVideoId(video.videoUrl);
    if (youtubeId) return `youtube:${youtubeId}`;
    const vimeoId = extractVimeoVideoId(video.videoUrl);
    if (vimeoId) return `vimeo:${vimeoId}`;
    return video.videoUrl;
  }
  return null;
};

/**
 * Get today's start timestamp (00:00:00)
 */
const getTodayStart = (): Date => {
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  return now;
};

/**
 * Calculate engagement score for a video based on today's activity
 */
export const calculateVideoEngagement = async (video: {
  id: string;
  videoUrl?: string;
  platformId?: string;
  platform?: 'youtube' | 'vimeo' | 'upload';
}): Promise<VideoEngagement> => {
  const todayStart = getTodayStart();
  const todayStartTimestamp = Timestamp.fromDate(todayStart);
  
  const videoIdentifier = normalizeVideoIdentifier(video);
  if (!videoIdentifier) {
    return {
      videoId: video.id,
      engagementScore: 0,
      reactions: 0,
      comments: 0,
      shares: 0,
      postsCount: 0
    };
  }

  try {
    // Find all posts from today that contain this video
    const postsQuery = query(
      collection(db, 'communityPosts'),
      where('timestamp', '>=', todayStartTimestamp),
      orderBy('timestamp', 'desc')
    );
    
    const postsSnapshot = await getDocs(postsQuery);
    const matchingPosts: string[] = [];
    
    postsSnapshot.docs.forEach(doc => {
      const post = doc.data();
      
      // Check if post contains this video
      let containsVideo = false;
      
      // Check content for YouTube/Vimeo links
      if (post.content) {
        const youtubeId = extractYouTubeVideoId(post.content);
        const vimeoId = extractVimeoVideoId(post.content);
        if (youtubeId && videoIdentifier.includes(`youtube:${youtubeId}`)) {
          containsVideo = true;
        }
        if (vimeoId && videoIdentifier.includes(`vimeo:${vimeoId}`)) {
          containsVideo = true;
        }
      }
      
      // Check linkPreviews for video URLs
      if (post.linkPreviews && Array.isArray(post.linkPreviews)) {
        for (const preview of post.linkPreviews) {
          if (preview.url && (
            preview.url === video.videoUrl ||
            videoIdentifier.includes(extractYouTubeVideoId(preview.url) || '') ||
            videoIdentifier.includes(extractVimeoVideoId(preview.url) || '')
          )) {
            containsVideo = true;
            break;
          }
        }
      }
      
      // Check attachments for video URLs
      if (post.attachments && Array.isArray(post.attachments)) {
        for (const attachment of post.attachments) {
          if (attachment.type === 'video' && attachment.url === video.videoUrl) {
            containsVideo = true;
            break;
          }
        }
      }
      
      if (containsVideo) {
        matchingPosts.push(doc.id);
      }
    });

    if (matchingPosts.length === 0) {
      return {
        videoId: video.id,
        videoUrl: video.videoUrl,
        platformId: video.platformId,
        platform: video.platform,
        engagementScore: 0,
        reactions: 0,
        comments: 0,
        shares: 0,
        postsCount: 0
      };
    }

    // Calculate total engagement from all matching posts
    let totalReactions = 0;
    let totalComments = 0;
    let totalShares = 0;

    // Get all matching posts in one query
    const postsToFetch = matchingPosts.slice(0, 10); // Limit to 10 posts for performance
    const postPromises = postsToFetch.map(async (postId) => {
      try {
        const { doc, getDoc } = await import('firebase/firestore');
        const postRef = doc(db, 'communityPosts', postId);
        const postSnap = await getDoc(postRef);
        return postSnap.exists() ? postSnap.data() : null;
      } catch (error) {
        console.error(`Error fetching post ${postId}:`, error);
        return null;
      }
    });

    const posts = await Promise.all(postPromises);

    posts.forEach((post) => {
      if (!post) return;
      
      // Count reactions
      if (post.reactions && typeof post.reactions === 'object') {
        Object.values(post.reactions).forEach((reactionArray: any) => {
          if (Array.isArray(reactionArray)) {
            totalReactions += reactionArray.length;
          }
        });
      }
      
      // Count comments
      if (post.comments && Array.isArray(post.comments)) {
        totalComments += post.comments.length;
      }
      
      // Count shares
      if (typeof post.shares === 'number') {
        totalShares += post.shares;
      }
    });

    // Calculate engagement score (weighted)
    // Reactions: 1 point, Comments: 3 points, Shares: 5 points
    const engagementScore = totalReactions + (totalComments * 3) + (totalShares * 5);

    return {
      videoId: video.id,
      videoUrl: video.videoUrl,
      platformId: video.platformId,
      platform: video.platform,
      engagementScore,
      reactions: totalReactions,
      comments: totalComments,
      shares: totalShares,
      postsCount: matchingPosts.length
    };
  } catch (error) {
    console.error('Error calculating video engagement:', error);
    return {
      videoId: video.id,
      videoUrl: video.videoUrl,
      platformId: video.platformId,
      platform: video.platform,
      engagementScore: 0,
      reactions: 0,
      comments: 0,
      shares: 0,
      postsCount: 0
    };
  }
};

/**
 * Get all videos with their today's engagement scores
 */
export const getAllVideoEngagements = async (videos: Array<{
  id: string;
  videoUrl?: string;
  platformId?: string;
  platform?: 'youtube' | 'vimeo' | 'upload';
}>): Promise<Map<string, VideoEngagement>> => {
  const engagements = new Map<string, VideoEngagement>();
  
  // Process videos in batches to avoid overwhelming Firestore
  const batchSize = 10;
  for (let i = 0; i < videos.length; i += batchSize) {
    const batch = videos.slice(i, i + batchSize);
    const batchPromises = batch.map(video => calculateVideoEngagement(video));
    const batchResults = await Promise.all(batchPromises);
    
    batchResults.forEach(engagement => {
      engagements.set(engagement.videoId, engagement);
    });
  }
  
  return engagements;
};

/**
 * Determine if a video is trending based on engagement threshold
 * Trending = top 20% of videos by engagement OR engagement score > 50
 */
export const isVideoTrending = (
  engagement: VideoEngagement,
  allEngagements: VideoEngagement[]
): boolean => {
  if (engagement.engagementScore === 0) return false;
  
  // Minimum threshold
  if (engagement.engagementScore < 10) return false;
  
  // Sort all engagements by score
  const sorted = [...allEngagements].sort((a, b) => b.engagementScore - a.engagementScore);
  
  // Top 20% are trending
  const top20PercentIndex = Math.floor(sorted.length * 0.2);
  const top20PercentScore = sorted[top20PercentIndex]?.engagementScore || 0;
  
  // Or if score is above 50 (absolute threshold)
  return engagement.engagementScore >= Math.max(top20PercentScore, 50);
};

